import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AxiosError } from "axios";

import { IGiveaway, IInvoice, ITicket } from "../../interfaces";

import { EmitError } from "../../utils/EmitError";
import useAuth from "../../hooks/useAuth";
import InvoicesService from "../../services/http/services/InvoicesService";
import { BalanceHistoryEntry, IInvoiceDetails } from "./interfaces";
import GiveawaysService from "../../services/http/services/GiveawaysService";
import { EInvoiceStatus } from "../../enums";
import TicketsService from "../../services/http/services/TicketsService";
import { EmitSuccessAlert } from "../../utils/EmitSuccessAlert";


export default function useInvoiceDetails() {
    const { id } = useParams();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isLoadingRaffle, setIsLoadingRaffle] = useState<boolean>(true);
    const [invoiceDetails, setInvoiceDetails] = useState<IInvoiceDetails>();
    const [isLoadingInvoices, setIsLoadingInvoices] = useState<boolean>(true);
    const [isReprocessing, setIsReprocessing] = useState<boolean>(false);
    const [raffleId, setRaffleId] = useState<number>();
    const [raffle, setRaffle] = useState<IGiveaway>();
    const [invoices, setInvoices] = useState<IInvoice[]>();
    const [tickets, setTickets] = useState<ITicket[]>();
    const [isLoadingTickets, setIsLoadingTickets] = useState<boolean>(true);

    const navigate = useNavigate();
    const { activeCity } = useAuth();

    const reloadControllerRef = useRef<AbortController | null>(null);

    const onViewUser = useCallback((id: number) => {
        navigate(`/usuarios/aplicativo/visualizar/${id}`);
    }, [navigate]);

    const onOpenInvoice = useCallback((id: number) => {
        navigate(`/nota/detalhes/${id}`);
    }, [navigate]);

    const buildBalanceHistory = useCallback(({ invoices, currentInvoice }: { invoices: IInvoice[]; currentInvoice: IInvoiceDetails; }): BalanceHistoryEntry[] => {
        const dividerTickets = invoiceDetails?.county?.divider_tickets;

        if (!dividerTickets) {
            return [];
        }

        const orderedInvoices = [...invoices].sort(
            (a, b) =>
                new Date(a.date_issue ?? a.created_at).getTime() -
                new Date(b.date_issue ?? b.created_at).getTime()
        );

        let saldo = 0;

        return orderedInvoices.map((invoice, index) => {
            const saldoAnterior = saldo;

            const isNFS = invoice.type === "NFS";

            const divisor = isNFS ? dividerTickets.value_nfs ?? 1 : dividerTickets.value_nfc ?? 1;

            const limite = isNFS ? dividerTickets.value_max_nfs : dividerTickets.value_max_nfc;

            const unlimited = isNFS ? dividerTickets.is_unlimited_nfs : dividerTickets.is_unlimited_nfc;

            const valorNota = invoice.value ?? 0;

            const valorConsiderado = unlimited ? valorNota : Math.min(valorNota, limite ?? valorNota);

            const saldoAposSoma = saldoAnterior + valorConsiderado;
            const bilhetesGerados = Math.floor(saldoAposSoma / divisor);
            const saldoRestante = saldoAposSoma % divisor;

            saldo = saldoRestante;

            return {
                ordem: index + 1,
                id: invoice.id,
                data: invoice.date_issue ?? String(invoice.created_at),
                valor: valorNota,
                tipo: invoice.type!,
                saldoAnterior,
                valorConsiderado,
                divisor,
                saldoAposSoma,
                bilhetesGerados,
                saldoRestante,
                bilhetesAssociados: invoice.tickets?.map(String) ?? [],
                notaAtual: invoice.id === currentInvoice.id,
            };
        });
    },
        [invoiceDetails?.county?.divider_tickets]
    );

    const reloadInvoice = useCallback(async () => {
        if (!activeCity || !id) return;

        reloadControllerRef.current?.abort();
        const controller = new AbortController();
        reloadControllerRef.current = controller;

        try {
            setIsLoading(true);

            const details = await InvoicesService.getInvoiceById(+id, controller.signal);

            setInvoiceDetails(details);

            if (details.raffle_id) {
                setRaffleId(details.raffle_id);
            } else {
                setRaffleId(undefined);
                setIsLoadingRaffle(false);
            }
        } catch (err) {
            if (err instanceof AxiosError && err.name === 'CanceledError') {
                return;
            }
            EmitError(err, true, 'invoice_details');
        } finally {
            setIsLoading(false);
        }
    }, [activeCity, id]);

    const reprocessInvoice = useCallback((invoiceId: number, appUserId?: number) => {

        async function sendInvoiceToReprocess() {

            try {
                setIsReprocessing(true);
                await InvoicesService.reprocessInvoice({
                    invoiceId,
                    appUserId: Number(appUserId),
                    cityId: activeCity?.id!
                });

                EmitSuccessAlert('Nota enviada para reprocessamento!');
                reloadInvoice();
                setIsReprocessing(false);
            } catch (err) {
                if (err instanceof AxiosError && err.name === 'CanceledError') {
                    return;
                }

                EmitError(err, true, 'reprocessInvoices');
                setIsReprocessing(false);
            }

        }

        sendInvoiceToReprocess();

    }, [activeCity, reloadInvoice]);

    const history: BalanceHistoryEntry[] = useMemo(() => {
        if (!invoices || !invoiceDetails) return [];

        const filteredInvoices = [...invoices].filter((invoice) => invoice.status === EInvoiceStatus.completed);

        return buildBalanceHistory({ invoices: filteredInvoices, currentInvoice: invoiceDetails });
    }, [invoices, invoiceDetails, buildBalanceHistory]);

    useEffect(() => {
        if (!activeCity || !id) return;

        const controller = new AbortController();

        async function loadInvoice() {
            try {
                setIsLoading(true);

                const details = await InvoicesService.getInvoiceById(+id!, controller.signal);

                setInvoiceDetails(details);

                if (details.raffle_id) {
                    setRaffleId(details.raffle_id);
                } else {
                    setRaffleId(undefined);
                    setIsLoadingRaffle(false);
                }
            } catch (err) {
                if (err instanceof AxiosError && err.name === 'CanceledError') {
                    return;
                }
                EmitError(err, true, 'invoice_details');
            } finally {
                setIsLoading(false);
            }
        }

        loadInvoice();

        return () => {
            controller.abort();
        };
    }, [activeCity, id]);

    useEffect(() => {
        if (!invoiceDetails) return;

        const controller = new AbortController();

        async function loadRaffle() {
            try {
                setIsLoadingRaffle(true);

                if (raffleId) {
                    const raffleDetails = await GiveawaysService.getGiveawayById({ id: raffleId, signal: controller.signal });
                    setRaffle(raffleDetails);
                    return;
                }

                const date = invoiceDetails!.date_issue ?? invoiceDetails!.created_at!;
                const cityId = invoiceDetails!.county?.id!;

                const raffleByDate = await GiveawaysService.getGiveawayByCountyIdAndDate({ cityId, date, signal: controller.signal });
                setRaffle(raffleByDate);
                setRaffleId(raffleByDate.id);
            } catch (err) {
                if (err instanceof AxiosError && err.name === 'CanceledError') {
                    return;
                }
                EmitError(err, true, 'raffle_details');
            } finally {
                setIsLoadingRaffle(false);
            }
        }

        loadRaffle();

        return () => {
            controller.abort();
        };
    }, [raffleId, invoiceDetails]);

    useEffect(() => {
        if (!raffle || !invoiceDetails) return;

        const controller = new AbortController();

        async function loadInvoices() {
            try {
                setIsLoadingInvoices(true);

                const startPeriod = raffle?.period_beginning;
                const endPeriod = raffle?.import_deadline;
                const appUserId = invoiceDetails?.app_user?.id;
                const cityId = invoiceDetails?.county?.id;

                if (!appUserId || !cityId || !startPeriod || !endPeriod) return;

                const invoicesList = await InvoicesService.listAllInvoicesByPeriodAndAppUserId({
                    appUserId,
                    cityId,
                    startPeriod,
                    endPeriod,
                    signal: controller.signal,
                });

                setInvoices(invoicesList);
            } catch (err) {
                if (err instanceof AxiosError && err.name === 'CanceledError') {
                    return;
                }
                EmitError(err, true, 'invoice_details');
            } finally {
                setIsLoadingInvoices(false);
            }
        }

        loadInvoices();

        return () => {
            controller.abort();
        };
    }, [raffle, invoiceDetails]);

    useEffect(() => {
        if (!raffleId) return;

        if (invoiceDetails?.app_user?.id === undefined) return;

        const controller = new AbortController();

        async function loadTickets() {
            try {

                setIsLoadingTickets(true);

                const ticketsList = await TicketsService.listTicketsByGiveawayIdAndAppUserId(
                    raffleId!,
                    invoiceDetails!.app_user?.id!,
                    controller.signal
                );

                setTickets(ticketsList);
            } catch (err) {
                if (err instanceof AxiosError && err.name === 'CanceledError') {
                    return;
                }
                EmitError(err, true, 'raffle_details');
            } finally {
                setIsLoadingTickets(false);
            }
        }

        loadTickets();

        return () => {
            controller.abort();
        };
    }, [raffleId, invoiceDetails]);

    return {
        isLoading,
        invoiceDetails,
        invoices,
        raffle,
        history,
        tickets,
        onViewUser,
        onOpenInvoice,
        reloadInvoice,
        isLoadingInvoices,
        isLoadingTickets,
        isLoadingRaffle,
        reprocessInvoice,
        isReprocessing
    }
}